// @flow

import React from 'react';
import { Translate } from 'react-redux-i18n';
import type { ShoppingCenter } from '../../../../types/shoppingCenterType';
import type { CatchmentAreaElement, CoqueRetailer } from '../../../../types/apolloType';
import CatchmentAreaMap from './CatchmentAreaMap';
import { secondsToMinutes } from '../../../../utils/dateUtils';
import { formatMeter, formatMinute } from '../../../../utils/formatUtils';
import googleServicesApi from '../../../../network/api/googleServicesApi';

type Props = {
  apiKey: string,
  shoppingCenter: ShoppingCenter,
  currentRetailer: CoqueRetailer,
}

type State = {
  catchmentAreaElements: CatchmentAreaElement[],
  isDistanceMatrix: boolean,
}

class CatchmentArea extends React.Component<Props, State> {
  state = {
    catchmentAreaElements: [],
    isDistanceMatrix: false,
  };

  componentDidMount() {
    this.fetchCatchmentAreaStores(this.props.shoppingCenter, this.props.currentRetailer);
  }

  shouldComponentUpdate(nextProps: Props, nextState: State, nextContext: any): boolean {
    if (this.props.currentRetailer.codeRetailerProposition !== nextProps.currentRetailer.codeRetailerProposition) {
      this.fetchCatchmentAreaStores(nextProps.shoppingCenter, nextProps.currentRetailer);
    }

    return true;
  }

  fetchCatchmentAreaStores = (shoppingCenter: ShoppingCenter, currentRetailer: CoqueRetailer) => {
    googleServicesApi.fetchCatchmentAreaStores(
      shoppingCenter.pcCode,
      currentRetailer.codeRetailerProposition,
      shoppingCenter.latitude,
      shoppingCenter.longitude,
      currentRetailer.codeStoreCoque,
      currentRetailer.retailerName,
    )
      .then(response => response.json())
      .then(response => this.setState({
        isDistanceMatrix: response.isDistanceMatrix,
        catchmentAreaElements: response.catchmentAreaElements,
      }));
  }

  getNearestPlaceDistance = (elements: CatchmentAreaElement[]): number => elements
    .reduce(
      (min: number, p: CatchmentAreaElement) =>
        (p.distanceToSc < min ? p.distanceToSc : min),
      elements[0].distanceToSc,
    );

  getNearestDistanceMatrixDuration = (elements: CatchmentAreaElement[]): number =>
    elements.reduce(
      (min: number, p: CatchmentAreaElement) =>
        (p.durationToSc < min ? p.durationToSc : min),
      elements[0].durationToSc,
    );

  getNumberOfStoreBetweenTimeDistanceMatrix = (elements: CatchmentAreaElement[], minimumTimeMinutes: number, maximumTimeMinutes: number): number =>
    elements
      .filter((e: CatchmentAreaElement) => minimumTimeMinutes < secondsToMinutes(e.durationToSc) && maximumTimeMinutes >= secondsToMinutes(e.durationToSc))
      .length;

  getNumberOfStoreBetweenDistance = (elements: CatchmentAreaElement[], minimumDistanceKilometer: number, maximumDistanceKilometer: number): number =>
    elements
      .filter((e: CatchmentAreaElement) => minimumDistanceKilometer * 1000 < e.distanceToSc && maximumDistanceKilometer * 1000 >= e.distanceToSc)
      .length;

  getNumberOfStoreBetweenDistanceOrTime = (isTime: boolean, elements: CatchmentAreaElement[], minimum: number, maximum: number): number =>
    (isTime
      ? this.getNumberOfStoreBetweenTimeDistanceMatrix(elements, minimum, maximum)
      : this.getNumberOfStoreBetweenDistance(elements, minimum, maximum));

  getNearestRetailerValueDistanceMatrix = () =>
    formatMinute(secondsToMinutes(this.getNearestDistanceMatrixDuration(this.state.catchmentAreaElements)));

  getNearestRetailerValuePlaces = () =>
    formatMeter(this.getNearestPlaceDistance(this.state.catchmentAreaElements));

  render() {
    const { catchmentAreaElements, isDistanceMatrix } = this.state;
    return (
      <div className="comparator-map-container">
        {
          this.props.apiKey
          && isDistanceMatrix ?
            (
              <CatchmentAreaMap
                apiKey={this.props.apiKey}
                shoppingCenter={this.props.shoppingCenter}
                elements={catchmentAreaElements}
                nearestRetailerText={<Translate value="comparator.numberOfRetailersNearby" />}
                isTime
                nearestRetailerValue={this.getNearestRetailerValueDistanceMatrix}
                getNumberOfStoreBetweenDistanceOrTime={this.getNumberOfStoreBetweenDistanceOrTime}
                numberOfStoreUnderTimeOrDistanceText={(time: number) =>
                  (<Translate
                    value="comparator.numbersOfRetailersInATimeLimit"
                    minute={time}
                  />)}
                numberOfStoreBetweenTimeOrDistanceText={(minimumMinute: number, maximumMinute: number) =>
                  (<Translate
                    value="comparator.numbersOfRetailersBetweenTwoTime"
                    minimumMinute={minimumMinute}
                    maximumMinute={maximumMinute}
                  />)}
              />
            ) : (
              <CatchmentAreaMap
                apiKey={this.props.apiKey}
                shoppingCenter={this.props.shoppingCenter}
                elements={catchmentAreaElements}
                nearestRetailerText={catchmentAreaElements[0] ?
                  <Translate
                    value="comparator.numbersOfRetailersInADistanceLimit"
                    km={catchmentAreaElements[0].searchRadius}
                  />
                  : ''
                }
                isTime={false}
                nearestRetailerValue={this.getNearestRetailerValuePlaces}
                getNumberOfStoreBetweenDistanceOrTime={this.getNumberOfStoreBetweenDistanceOrTime}
                numberOfStoreUnderTimeOrDistanceText={(distance: number) =>
                  (<Translate
                    value="comparator.numbersOfRetailersInADistanceLimit"
                    km={distance}
                  />)}
                numberOfStoreBetweenTimeOrDistanceText={(minimumKm: number, maximumKm: number) =>
                  (<Translate
                    value="comparator.numbersOfRetailersBetweenTwoDistance"
                    minimumKm={minimumKm}
                    maximumKm={maximumKm}
                  />)}
              />
            )
        }
      </div>
    );
  }
}

export default CatchmentArea;
